import {
    useContext,
    useEffect,
    useState,
    useTransition,
} from 'react';
import {
    useSearchParams,
} from 'react-router-dom';

import {
    ArrowBackIosRounded,
    ArrowForwardIosRounded,
    ArrowDownwardOutlined,
    Edit,
    Today,
    MoreVertRounded,
    ZoomIn,
} from '@mui/icons-material';
import {
    Box,
    Button,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    DataGrid
} from '@mui/x-data-grid';
import {
    formatISO,
    startOfMonth,
} from 'date-fns';


import WarningDialog from './WarningDialog';

import JSZip from "jszip";
import api from '../../api';
import { updateUserWorkDays } from '../../context/actions';
import { Context } from '../../context/context';
import DateFormat from '../../utils/DateFormat';
import TimeCalculator from '../../utils/TimeCalculator';
import EditWorkDayActionItem from './EditWorkDayActionItem';

import generatePDFfromRecords from '../../utils/PDFGenerator';
import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import StatusChip from '../status-overview/StatusChip';

function SubmitsPage() {
    const { t } = useTranslation()
    const [{ user }, dispatch] = useContext(Context);
    const [urlParam, setUrlParam] = useSearchParams({ 'month': '' });
    const [selectedMonth, setSelectedMonth] = useState(urlParam.get('month') ? new Date(urlParam.get('month')) : startOfMonth(new Date()));
    const [menuAnchEl, setMenuAnchEl] = useState(null);
    const isGenerateMenuOpen = Boolean(menuAnchEl);
    const [workDays, setWorkDays] = useState([]);
    const [rows, setRows] = useState([]);
    const [submittable, setSubmittable] = useState(false);
    const [warnDialogOpen, setWarnDialogOpen] = useState(false)
    const [missingWorkDays, setMissingWorkDays] = useState(0)
    const [missingClosingDays, setMissingClosingDays] = useState(0)

    const handleOpen = (e) => setMenuAnchEl(e.currentTarget);
    const handleClose = () => setMenuAnchEl(null);

    const handleMonthForward = () => {
        let newDate = new Date(selectedMonth)
        newDate.setMonth(selectedMonth.getMonth() + 1);

        setUrlParam({ 'month': formatISO(newDate).split('T')[0] });
        setSelectedMonth(newDate);
    };

    const handleMonthBack = () => {
        let newDate = new Date(selectedMonth);
        newDate.setMonth(newDate.getMonth() - 1);

        setUrlParam({ 'month': formatISO(newDate).split('T')[0] });
        setSelectedMonth(newDate);
    };

    const handleCurrentMonth = () => {
        const currentMonth = startOfMonth(new Date());

        setUrlParam({ 'month': formatISO(currentMonth).split('T')[0] });
        setSelectedMonth(currentMonth);
    };

    const checkSubmittable = (workDays) => {
        if (!workDays) {
            return true;
        };

        if (workDays.length === 0) {
            return true;
        };

        return !workDays[0].submitted;
    };

    useEffect(() => {
        let monthsWorkDays = user.workDays.filter(wd => {
            return wd.date.getMonth() === selectedMonth.getMonth() && wd.date.getFullYear() === selectedMonth.getFullYear();
        });

        let isSubmitable = checkSubmittable(monthsWorkDays);

        setSubmittable(isSubmitable);
        setWorkDays(monthsWorkDays);
    }, [user.workDays, selectedMonth]);

    useEffect(() => {
        let rows = workDays.filter(wd => wd.workEntries.length > 0 || wd.contractWorkLoad > 0).map(wd => {
            const {
                day,
                start,
                end,
                sumWorkDurations,
                sumBreakDurations,
            } = TimeCalculator.computeTimesFromWorkDay(wd);

            const time = `${DateFormat.formatHHMM(start)} - ${DateFormat.formatHHMM(end)}`;
            const hours = DateFormat.formatDuration(sumWorkDurations);
            const breakTime = DateFormat.formatDuration(sumBreakDurations);

            return {
                id: wd.id,
                day: day.toLocaleDateString(i18n.language, { weekday: 'short' }),
                date: DateFormat.formatDDMM(day),
                times: wd.start ? time : '',
                break: breakTime,
                duration: hours,
            };
        });

        setRows(rows);
    }, [workDays]);

    const handleSubmitMonth = () => {
        let month = formatISO(selectedMonth).split('T')[0];

        api.submitMonths(user.id, [month]).then(res => {
            api.readUser(user.id).then(res => {
                dispatch(updateUserWorkDays(res.workDays));
            });
        });
    };

    const handleGenerateRecord = () => {
        let start = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
        let end = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);
        let zip = new JSZip();
        let folderName = `Arbeitsnachweise  ${DateFormat.formatDDMMYY(start)} - ${DateFormat.formatDDMMYY(end)}`;
        let folder = zip.folder(folderName);

        const wr = {
            ...user,
            workDays: workDays.filter(wd => wd.workEntries.length > 0),
        };

        generatePDFfromRecords([wr], true, start, end, user.name).then(res => {
            folder.file(
                `${user.name} ${DateFormat.formatDDMMYY(start)}.pdf`,
                res[0],
            );

            folder.generateAsync({ type: 'blob' }).then(blob => {
                const element = document.createElement("a");
                element.download = `${folderName}.zip`;
                element.href = URL.createObjectURL(blob);
                document.body.appendChild(element);
                element.click();
            });
        });
    };

    const prepareSubmit = () => {

        let end = new Date(Date.UTC(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0));
        let endDate = formatISO(end).split('T')[0];

        let lastDay = user.workDays.find(wd => {
            let d = formatISO(wd.date).split('T')[0]
            if (d === endDate) {
                return true;
            }
            return false;
        });

        if (!lastDay) {

            let wd = {
                date: end,
                start: null,
                end: null,
                compensation: null,
                workEntries: [],
            };

            api.createWorkDay(user.id, wd).then(res => {
                api.readUser(user.id).then(res => {
                    let monthsWorkDays = res.workDays.filter(wd => {

                        return wd.date.getMonth() === selectedMonth.getMonth() && wd.date.getFullYear() === selectedMonth.getFullYear();
                    });
                    dispatch(updateUserWorkDays(res.workDays));
                    checkSubmitWarning(monthsWorkDays);
                });
            });
        } else {
            checkSubmitWarning(workDays)
        }

    }

    const checkSubmitWarning = (monthsWorkDays) => {
        let sumWorkDaysNoEntries = 0;
        let sumBridgeDaysNoEntries = 0;

        for (const wd of monthsWorkDays) {
            let needsEntries = wd.contractWorkLoad > 0;
            let hasEntries = wd.workEntries.length > 0;
            let hasCompensation = wd.compensation > 0;

            if (!(hasEntries || hasCompensation)) {
                if (needsEntries) {
                    let date = formatISO(wd.date).split('T')[0];
                    let isClosingDay = false;

                    for (const closingDay of user.location.closingDays) {
                        let closeDate = formatISO(closingDay).split('T')[0];

                        if (date === closeDate) {
                            isClosingDay = true;
                            break;
                        };
                    };

                    if (isClosingDay) {
                        sumBridgeDaysNoEntries++;
                    } else {
                        sumWorkDaysNoEntries++;
                    };
                };
            };
        };

        if (sumBridgeDaysNoEntries > 0 || sumWorkDaysNoEntries > 0) {
            setMissingClosingDays(sumBridgeDaysNoEntries);
            setMissingWorkDays(sumWorkDaysNoEntries);
            setWarnDialogOpen(true);
        } else {
            handleSubmitMonth();
        };
    };



    const columns = [
        { field: 'day', headerName: t('tag', { ns: 'submitsPage', defaultValue: 'Tag' }), flex: 1, sortable: false },
        { field: 'date', headerName: t('datum', { ns: 'submitsPage', defaultValue: 'Datum' }), flex: 1 },
        { field: 'times', headerName: t('times', { ns: 'submitsPage', defaultValue: 'Zeiten' }), flex: 2, sortable: false },
        { field: 'duration', headerName: t('arbeitszeitNetto', { ns: 'submitsPage', defaultValue: 'Arbeitszeit (netto)' }), flex: 2, sortable: false },
        { field: 'break', headerName: t('pausen', { ns: 'submitsPage', defaultValue: 'Pausen' }), flex: 2, sortable: false },
        // {
        //     field: 'submitted',
        //     headerName: 'Status',
        //     renderCell: (params) => {
        //         return (
        //             <StatusChip status={!submittable} />
        //         )
        //     },
        //     flex: 1,
        //     sortable: false,

        // },
        {
            field: 'actions',
            headerName: t(submittable ? 'bearbeiten' : 'inspizieren', { ns: 'submitsPage', defaultValue: submittable ?  'Bearbeiten' : 'Ansehen' }),
            description: 'Link zur detaillierten Zeiterfassung',
            type: 'actions',
            flex: 0,
            getActions: (params) => {
                return [
                    <EditWorkDayActionItem
                        label="Arbeitseintrag bearbeiten"
                        icon={submittable ? <Edit /> : <ZoomIn />}
                        id={params.row.id}
                    />
                ]
            },
        },
    ]

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: 'fit-content', gap: 1 }}>
                    <Tooltip title={t('aktuellerMonat', { ns: 'submitsPage', defaultValue: 'Zum aktuellen Monat' })}>
                        <IconButton size='small' onClick={handleCurrentMonth}>
                            <Today />
                        </IconButton>
                    </Tooltip>
                    <IconButton size='small' onClick={handleMonthBack}>
                        <ArrowBackIosRounded fontSize='inherit' />
                    </IconButton>
                    <IconButton size='small' onClick={handleMonthForward}>
                        <ArrowForwardIosRounded fontSize='inherit' />
                    </IconButton>
                    <Typography sx={{ fontWeight: 'bold' }}>
                        {selectedMonth.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', width: 'fit-content', alignItems: 'center', gap: 1 }}>
                    <Button variant='contained' disabled={!submittable} onClick={prepareSubmit}>
                        {submittable ? t('monatFreigeben', { ns: 'submitsPage', defaultValue: 'Monat Freigeben' }) : t('monatFreigegeben', { ns: 'submitsPage', defaultValue: 'Monat Freigegeben' })}
                    </Button>
                    <IconButton onClick={handleOpen} >
                        <MoreVertRounded />
                    </IconButton>
                    <Menu
                        anchorEl={menuAnchEl}
                        open={isGenerateMenuOpen}
                        onClose={handleClose}
                    >
                        <MenuItem
                            onClick={handleGenerateRecord}
                        >
                            <ListItemIcon>
                                <ArrowDownwardOutlined fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{t('nachweisGenerieren', { ns: 'submitsPage', defaultValue: 'Nachweis erstellen' })}</ListItemText>
                        </MenuItem>
                    </Menu>
                    <WarningDialog
                        open={warnDialogOpen}
                        onClose={() => setWarnDialogOpen(false)}
                        onSubmit={() => {
                            handleSubmitMonth()
                            setWarnDialogOpen(false)
                        }}
                        missingClosingDays={missingClosingDays}
                        missingWorkDays={missingWorkDays}
                        month={selectedMonth}
                        isFlexTimeAccount={user.hasFlexTimeAccount}
                    />
                </Box>
            </Box>

            <DataGrid
                columns={columns}
                rows={rows}
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'date', sort: 'asc' }],
                    },
                }}
                disableColumnMenu
                disableColumnFilter
                slots={{
                    noRowsOverlay: () => "", // otherwise a 1px no Rows will be displayed
                }}
            />
        </>
    );
}

export default SubmitsPage;
